import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import itemsApi from '../itemsApi';
import { loadSalt } from '../authentication/authenticationReducer';
import { addSuccessNotification, addErrorNotification } from '../common/notification/notificationReducer';
import transformer from '../common/helpers/transformer';
import type { Client } from '../common/types/client';
import type { DecryptedItem } from '../common/types/items';
import type { AppDispatch, ClientsState, Store } from '../store';
import { ClientPayloadType } from '../common/types/general';

const initialState: ClientsState = {
  clients: [],
  currentClient: null,
  status: null,
  errorMessage: null,
};

const loadClients = createAsyncThunk<
  DecryptedItem<Client>[],
  ClientPayloadType,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('clients/loadClients', async (payloadType, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  await dispatch(loadSalt());
  const data = await itemsApi.loadItems(payloadType);
  if (data != null) {
    return await Promise.all(
      data.items.map(async (item): Promise<DecryptedItem<Client>> => {
        try {
          const client = await transformer.fromBase64ToObjectWithDecryption<Client>(item.payload);
          return {
            ...item,
            payload: client,
          };
        } catch (error) {
          console.log('Fehler: ', error);
          dispatch(addErrorNotification('Klient*in konnte nicht entschlüsselt werden.'));
          return {
            ...item,
            payload: {},
          };
        }
      })
    );
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Klient*innen konnten nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const loadClientById = createAsyncThunk<
  DecryptedItem<Client> | null,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('clients/loadClientById', async (id: string, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const data = await itemsApi.loadItemById(id);
  if (data != null && data.item != null) {
    const client = await transformer.fromBase64ToObjectWithDecryption<Client>(data.item.payload);
    return {
      ...data.item,
      payload: client,
    };
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Der/Die Klient*in mit der ID ${id} konnte nicht geladen werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const addClient = createAsyncThunk<
  string | null,
  { id: string; payloadType: ClientPayloadType; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('clients/addClient', async ({ id, payloadType, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const item = await itemsApi.addItem(id, payload, payloadType);
  if (item != null) {
    dispatch(addSuccessNotification('Der/Die Klient*in wurde hinzugefügt.'));
    return item.id;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Der/Die Klient*in mit der ID ${id} konnte nicht hinzugefügt werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const editClient = createAsyncThunk<
  boolean,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('clients/editClient', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isEdited = await itemsApi.editItem(id, payload);
  if (isEdited) {
    dispatch(addSuccessNotification('Die Änderungen wurden gespeichert.'));
    return isEdited;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Änderungen konnten nicht gespeichert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const deleteClient = createAsyncThunk<
  boolean,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('clients/deleteClient', async (id, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isDeleted = await itemsApi.deleteItem(id);
  if (isDeleted) {
    dispatch(addSuccessNotification('Der/Die Klient*in wurde gelöscht.'));
    return isDeleted;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Der/Die Klient*in mit der ID ${id} konnte nicht gelöscht werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearCurrentClient(state) {
      state.currentClient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadClients.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadClients.fulfilled, (state, action) => {
        state.status = 'idle';
        state.clients = action.payload;
      })
      .addCase(loadClients.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(loadClientById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadClientById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentClient = action.payload;
      })
      .addCase(loadClientById.rejected, (state, action) => {
        state.status = 'idle';
        state.currentClient = null;
        state.errorMessage = action.payload;
      })
      .addCase(addClient.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addClient.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(addClient.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(editClient.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editClient.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(editClient.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(deleteClient.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

const { clearCurrentClient } = clientsSlice.actions;

export { loadClients, loadClientById, clearCurrentClient, addClient, editClient, deleteClient };

export default clientsSlice.reducer;
